import React, { FC, useEffect, useMemo, useState } from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { Fancybox as NativeFancybox } from '@fancyapps/ui'
import '@fancyapps/ui/dist/fancybox/fancybox.css'
import { fetchData, toasterrormsg, toastsuccessmsg } from '../../../reusable'
import { Tr } from '../../../_metronic/i18n/Tr'
import { KTIcon } from '../../../_metronic/helpers'

// entity types accepted by vendor/public/updateMediaSortOrder
export type MediaSortType =
  | 'hotel'
  | 'room'
  | 'restaurant'
  | 'restaurantmenu'
  | 'spacerental'
  | 'rentablespace'
  | 'tourpackage'
  | 'tours'
  | 'carrental'
  | 'vehicle'

// media rows come back with a different id field per entity, and a few
// entities reuse another entity's field (room media uses hotelmediaId), so the
// id is resolved from an explicit key first and then from the known set.
const KNOWN_ID_KEYS = [
  'hotelmediaId',
  'roommediaId',
  'restaurantmediaId',
  'restaurantmenumediaId',
  'menumediaId',
  'spacerentalmediaId',
  'rentablespacemediaId',
  'tourpackagemediaId',
  'toursmediaId',
  'tourmediaId',
  'carrentalmediaId',
  'vehiclemediaId',
  'mediaId',
]

export const resolveMediaId = (item: any, idKey?: string) => {
  if (!item) return ''
  if (idKey && item[idKey] != undefined && item[idKey] != null) return item[idKey]
  for (const key of KNOWN_ID_KEYS) {
    if (item[key] != undefined && item[key] != null) return item[key]
  }
  return ''
}

const orderKey = (images: any[], idKey?: string) =>
  (images || []).map((image: any) => String(resolveMediaId(image, idKey))).join(',')

// same ids regardless of position - tells a reorder apart from an add/delete
const membershipKey = (images: any[], idKey?: string) =>
  (images || [])
    .map((image: any) => String(resolveMediaId(image, idKey)))
    .sort()
    .join(',')

// sends the current order of already-uploaded media, first item = first shown
export const updateMediaSortOrder = async (
  type: MediaSortType,
  images: any[],
  adminId: any,
  idKey?: string
) => {
  const mediaIds = (images || [])
    .map((image: any) => resolveMediaId(image, idKey))
    .filter((id: any) => id != '' && id != undefined && id != null)
    .map((id: any) => String(id))

  if (mediaIds.length < 2) return true

  var param = {
    type: type,
    mediaIds: mediaIds,
    adminId: adminId ? adminId : 0,
  }
  var res = await fetchData('vendor/public/updateMediaSortOrder', param)

  if (res.status == '200') return true

  toasterrormsg(res.message)
  return false
}

const moveItem = (images: any[], from: number, to: number) => {
  const items = Array.from(images)
  const [moved] = items.splice(from, 1)
  items.splice(to, 0, moved)
  return items
}

// lightbox scoped to one gallery, so other galleries on the page keep their own
// binding - unbind instead of destroy for the same reason
const useGalleryLightbox = (group: string) => {
  useEffect(() => {
    const delegate = `[data-fancybox="${group}"]`
    NativeFancybox.bind(delegate, {
      idle: false,
      dragToClose: false,
      Thumbs: false,
      Toolbar: {
        display: {
          left: [],
          middle: [],
          right: ['close'],
        },
      },
    })
    return () => {
      NativeFancybox.unbind(delegate)
      NativeFancybox.close()
    }
  }, [group])
}

type TileProps = {
  src: string
  index: number
  fancyboxGroup: string
  dragging: boolean
  disabled: boolean
  onRemove: () => void
  removeLabel: string
}

// one thumbnail: position badge, remove button and lightbox trigger
const MediaTile: FC<TileProps> = ({
  src,
  index,
  fancyboxGroup,
  dragging,
  disabled,
  onRemove,
  removeLabel,
}) => (
  <>
    <button
      type='button'
      className='btn btn-icon btn-sm btn-primary rounded-circle'
      title={removeLabel}
      disabled={disabled}
      style={{
        width: '22px',
        height: '22px',
        minWidth: '22px',
        padding: 0,
        lineHeight: 1,
        alignSelf: 'end',
        position: 'relative',
        top: '7%',
        right: '-3%',
        zIndex: 2,
      }}
      onClick={onRemove}
    >
      <KTIcon iconName='cross' iconType='solid' className='fs-5' />
    </button>
    <div
      className='p-2 shadow rounded-1 position-relative'
      style={{ maxWidth: 'fit-content', opacity: dragging ? 0.85 : 1 }}
    >
      <a data-fancybox={fancyboxGroup} href={src} className='d-block'>
        <img alt='images' src={src} width='100%' className='rounded-1' draggable={false} />
      </a>
      <span
        className='badge badge-circle badge-dark position-absolute'
        style={{ bottom: '12px', left: '12px', fontSize: '10px' }}
      >
        {index + 1}
      </span>
    </div>
  </>
)

type Props = {
  type: MediaSortType
  images: any[]
  // applies the reordered list to the parent's state
  onReorder: (images: any[]) => void
  onRemove: (mediaId: any) => void
  adminId: any
  idKey?: string
  pathKey?: string
  droppableId?: string
}

const SortableUploadedImages: FC<Props> = ({
  type,
  images,
  onReorder,
  onRemove,
  adminId,
  idKey,
  pathKey = 'mediaPath',
  droppableId = 'uploadedImageList',
}) => {
  let t = Tr()
  const [saving, setSaving] = useState(false)
  // order as the server currently has it, so the save button only shows when
  // the on screen order actually differs from it
  const [savedOrder, setSavedOrder] = useState(() => orderKey(images, idKey))

  const fancyboxGroup = `sortable-media-${droppableId}`
  useGalleryLightbox(fancyboxGroup)

  // an image was added or deleted, so whatever the parent just loaded counts as
  // the saved order - only a drag should make the save button appear
  const currentMembership = membershipKey(images, idKey)
  useEffect(() => {
    const savedMembership = savedOrder.split(',').sort().join(',')
    if (currentMembership != savedMembership) {
      setSavedOrder(orderKey(images, idKey))
    }
  }, [currentMembership])

  const isDirty = orderKey(images, idKey) != savedOrder

  const handleDragEnd = (result: any) => {
    if (!result.destination) return // dropped outside the list
    if (result.destination.index == result.source.index) return
    onReorder(moveItem(images, result.source.index, result.destination.index))
  }

  const handleSaveOrder = async () => {
    setSaving(true)
    const saved = await updateMediaSortOrder(type, images, adminId, idKey)
    setSaving(false)
    if (saved) {
      setSavedOrder(orderKey(images, idKey))
      toastsuccessmsg('Image order updated successfully!')
    }
  }

  if (!images || images.length == 0) return null

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={droppableId} direction='horizontal'>
          {(provided: any) => (
            <div
              className='d-flex flex-wrap mt-3 w-100'
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {images.map((imageList: any, index: any) => {
                const mediaId = resolveMediaId(imageList, idKey)
                const draggableId = mediaId != '' ? `media-${mediaId}` : `media-index-${index}`
                return (
                  <Draggable
                    key={draggableId}
                    draggableId={draggableId}
                    index={index}
                    isDragDisabled={saving}
                  >
                    {(provided: any, snapshot: any) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className='col-lg-2 col-4 d-flex flex-column mb-5 px-2'
                        style={{
                          cursor: saving ? 'wait' : 'move',
                          ...provided.draggableProps.style,
                        }}
                      >
                        <MediaTile
                          src={imageList[pathKey]}
                          index={index}
                          fancyboxGroup={fancyboxGroup}
                          dragging={snapshot.isDragging}
                          disabled={saving}
                          onRemove={() => onRemove(mediaId)}
                          removeLabel={t('Remove')}
                        />
                      </div>
                    )}
                  </Draggable>
                )
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {isDirty && (
        <div className='d-flex align-items-center gap-3 mb-5 w-100'>
          <button
            type='button'
            className='btn btn-sm btn-primary'
            disabled={saving}
            onClick={handleSaveOrder}
          >
            <span className='indicator-label'>{t('Save Image Order')}</span>
            {saving && (
              <span
                className='spinner-border spinner-border-sm mx-2'
                role='status'
                aria-hidden='true'
              ></span>
            )}
          </button>
          <span className='text-muted fs-7'>{t('Image order changed, not saved yet.')}</span>
        </div>
      )}
    </>
  )
}

type NewImagesProps = {
  // File objects picked in the file input, not uploaded yet
  images: any[]
  onReorder: (images: any[]) => void
  onRemove: (index: number) => void
  droppableId?: string
}

// the not-yet-uploaded grid: reordering here decides the order the files are
// sent in, which is the order the backend stores them in
const SortableNewImages: FC<NewImagesProps> = ({
  images,
  onReorder,
  onRemove,
  droppableId = 'newImageList',
}) => {
  let t = Tr()
  const fancyboxGroup = `sortable-new-media-${droppableId}`
  useGalleryLightbox(fancyboxGroup)

  // one object url per file, revoked when the file leaves the list, otherwise
  // a new url would leak on every render
  const previews = useMemo(
    () => (images || []).map((file: any) => (file instanceof File ? URL.createObjectURL(file) : file)),
    [images]
  )
  useEffect(() => {
    return () => {
      previews.forEach((url: string) => {
        if (typeof url == 'string' && url.startsWith('blob:')) URL.revokeObjectURL(url)
      })
    }
  }, [previews])

  const handleDragEnd = (result: any) => {
    if (!result.destination) return // dropped outside the list
    if (result.destination.index == result.source.index) return
    onReorder(moveItem(images, result.source.index, result.destination.index))
  }

  if (!images || images.length == 0) return null

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={droppableId} direction='horizontal'>
        {(provided: any) => (
          <div
            className='d-flex flex-wrap mt-3 w-100'
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {images.map((file: any, index: any) => (
              <Draggable
                key={`new-image-${index}-${file?.name ?? ''}`}
                draggableId={`new-image-${index}-${file?.name ?? ''}`}
                index={index}
              >
                {(provided: any, snapshot: any) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className='col-lg-2 col-4 d-flex flex-column mb-5 px-2'
                    style={{ cursor: 'move', ...provided.draggableProps.style }}
                  >
                    <MediaTile
                      src={previews[index]}
                      index={index}
                      fancyboxGroup={fancyboxGroup}
                      dragging={snapshot.isDragging}
                      disabled={false}
                      onRemove={() => onRemove(index)}
                      removeLabel={t('Remove')}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}

export { SortableUploadedImages, SortableNewImages }
